import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { getAuthHeaders } from '../utils/authHeaders';

const API_URL = 'https://app.pvslimited.co.uk/api';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
};

const buildGasFreeCheckPdf = async (formData) => {
  const pdfDoc = await PDFDocument.create();
  const page = pdfDoc.addPage([595.28, 841.89]); // A4 portrait
  const { width, height } = page.getSize();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const navy = rgb(2 / 255, 12 / 255, 105 / 255);
  const dark = rgb(0.13, 0.13, 0.13);
  const muted = rgb(0.4, 0.4, 0.4);
  const lineColor = rgb(0.8, 0.8, 0.8);

  const marginX = 50;
  let y = height - 60;

  page.drawText('GAS FREE CHECK', {
    x: marginX,
    y,
    size: 22,
    font: boldFont,
    color: navy,
  });

  y -= 18;
  page.drawText('Ref: PVS-GFC-001 | Version: 1.0', {
    x: marginX,
    y,
    size: 9,
    font,
    color: muted,
  });

  y -= 16;
  page.drawLine({
    start: { x: marginX, y },
    end: { x: width - marginX, y },
    thickness: 1.5,
    color: navy,
  });

  y -= 36;

  const rows = [
    ['Name', formData.userName || 'N/A'],
    ['Date', formatDate(formData.date) || 'N/A'],
    ['Serial Number', formData.serialNumber || 'N/A'],
    ['Water Capacity', formData.waterCapacity || 'N/A'],
    ['Owner', formData.owner || 'N/A'],
    ['LEL %', formData.lel !== '' && formData.lel !== undefined && formData.lel !== null ? `${formData.lel}%` : 'N/A'],
  ];

  const labelX = marginX;
  const valueX = marginX + 160;
  const rowHeight = 34;

  rows.forEach(([label, value]) => {
    page.drawText(label, { x: labelX, y, size: 11, font: boldFont, color: navy });
    page.drawText(String(value), { x: valueX, y, size: 11, font, color: dark });
    y -= 12;
    page.drawLine({
      start: { x: marginX, y },
      end: { x: width - marginX, y },
      thickness: 0.5,
      color: lineColor,
    });
    y -= rowHeight - 12;
  });

  y -= 10;
  page.drawText(
    `Generated ${new Date().toLocaleString('en-GB', { timeZone: 'Europe/London' })}`,
    { x: marginX, y, size: 8, font, color: muted }
  );

  const bytes = await pdfDoc.save();
  return new Blob([bytes], { type: 'application/pdf' });
};

const sendGasFreeCheckEmail = async (formData) => {
  try {
    const blob = await buildGasFreeCheckPdf(formData);
    const formattedDate = formatDate(formData.date);
    const subject = `Gas Free Check - ${formData.serialNumber || 'N/A'} - ${formattedDate}`;
    const safeSerial = String(formData.serialNumber || 'unknown').replace(/[^a-zA-Z0-9-_]/g, '_');
    const filename = `Gas_Free_Check_${safeSerial}_${formData.date}_${Date.now()}.pdf`;

    const body = new FormData();
    body.append('file', blob, filename);
    body.append('userName', formData.userName || '');
    body.append('date', formData.date || '');
    body.append('serialNumber', formData.serialNumber || '');
    body.append('waterCapacity', formData.waterCapacity || '');
    body.append('owner', formData.owner || '');
    body.append('lel', formData.lel !== undefined && formData.lel !== null ? String(formData.lel) : '');
    body.append('subject', subject);

    const response = await fetch(`${API_URL}/send-gas-free-check-email`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body,
    });

    if (!response.ok) {
      let message = 'Failed to send email';
      try {
        const payload = await response.json();
        if (payload?.error) message = payload.error;
      } catch {
        // keep default message
      }
      throw new Error(message);
    }
    return true;
  } catch (error) {
    console.error('Gas Free Check email sending failed:', error);
    throw error;
  }
};

export default sendGasFreeCheckEmail;
